/**
 * Nhật ký lịch phát thiết bị — `/lich-phat/nhat-ky-gui`.
 *
 * Mỗi dòng là một gói DV006: thiết bị/bộ tương thích báo đã nhận lịch phát.
 * Dữ liệu từ `GET /api/v1/broadcast/schedule-logs` (hợp đồng A0); lọc theo địa bàn
 * dạng cây, tên/mã lịch (kính lúp ở tiêu đề cột) và khoảng ngày gửi; lọc, đếm,
 * phân trang ở phía máy chủ, mặc định 20 dòng.
 *
 * "Xuất Excel" tải tệp .xlsx do máy chủ dựng (`GET /api/v1/broadcast/schedule-logs/export`)
 * cho TOÀN BỘ kết quả theo bộ lọc hiện tại, tối đa 10.000 dòng (29/10/2026, thay bản CSV dựng
 * ở trình duyệt).
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viLichNhatKy } from '../ngon-ngu/vi-lich-nhat-ky';
import { viDanhSachLichPhat } from '../ngon-ngu/vi-danh-sach-lich-phat';
import { phatSongApi, type BoLocNhatKyLichThietBi, type DongNhatKyLichThietBi } from '../api/phat-song-api';
import { thietBiApi, type NutDiaBan, type TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TruongLoc } from '../thanh-phan/MauGiaoDien';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { ngayVnSangEpoch, OKhoangNgay } from './DanhSachLichPhat';
import './danh-sach-lich-phat.css';
import './nhat-ky-lich-phat-thiet-bi.css';

const t = viLichNhatKy.nhatKyGuiLich;
const tds = viDanhSachLichPhat;

/** Giới hạn dòng của tệp xuất (máy chủ cắt ở cùng mức). */
export const XUAT_TOI_DA_DONG = 10_000;

export interface NhatKyLichPhatThietBiApi {
  nhatKy: (tham: BoLocNhatKyLichThietBi, signal?: AbortSignal) => Promise<TrangDuLieu<DongNhatKyLichThietBi>>;
  /** Tải tệp .xlsx theo bộ lọc; trả số dòng đã xuất và tổng số dòng khớp lọc. */
  xuatExcel: (tham: BoLocNhatKyLichThietBi) => Promise<{ soDong: number; tong: number }>;
  cayDiaBan: (tham: Record<string, never>, signal?: AbortSignal) => Promise<NutDiaBan[]>;
}

export const API_NHAT_KY_LICH_THIET_BI_MAC_DINH: NhatKyLichPhatThietBiApi = {
  nhatKy: phatSongApi.nhatKyLichThietBi,
  xuatExcel: phatSongApi.xuatExcelNhatKyLich,
  cayDiaBan: (tham, signal) => thietBiApi.cayDiaBan(tham, signal),
};

export interface NhatKyLichPhatThietBiProps {
  api?: NhatKyLichPhatThietBiApi;
}

/* --------------------------------- Tiện ích --------------------------------- */

const DINH_DANG_GIO_VN = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Ho_Chi_Minh',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function phanGioVn(ms: number): Record<'year' | 'month' | 'day' | 'hour' | 'minute', string> {
  const p: Record<string, string> = {};
  for (const x of DINH_DANG_GIO_VN.formatToParts(new Date(ms))) p[x.type] = x.value;
  return {
    year: p.year ?? '',
    month: p.month ?? '',
    day: p.day ?? '',
    hour: p.hour ?? '',
    minute: p.minute ?? '',
  };
}

/** Epoch giây → "HH:mm dd/MM/yyyy" theo giờ Việt Nam, không phụ thuộc múi giờ máy xem. */
export function gioNgayVn(epochGiay: number): string {
  if (!Number.isFinite(epochGiay)) return '—';
  const p = phanGioVn(epochGiay * 1000);
  return `${p.hour}:${p.minute} ${p.day}/${p.month}/${p.year}`;
}

/* ------------------------------ Ô tìm ở tiêu đề ------------------------------ */

function TimTieuDe({ tuKhoa, onTim }: { tuKhoa: string; onTim: (tuKhoa: string) => void }): ReactNode {
  const [mo, datMo] = useState(false);
  const [oTim, datOTim] = useState(tuKhoa);

  const gui = (e: FormEvent): void => {
    e.preventDefault();
    onTim(oTim.trim());
    datMo(false);
  };

  return (
    <span className="nklt-tim">
      <button
        type="button"
        className={`nklt-tim__mo${tuKhoa ? ' nklt-tim__mo--dang-loc' : ''}`}
        aria-label={t.timTheoTenMa}
        title={t.timTheoTenMa}
        aria-expanded={mo}
        data-hanh-dong="mo-tim-lich"
        onClick={() => {
          datOTim(tuKhoa);
          datMo((x) => !x);
        }}
      >
        <BieuTuong ten="tim-kiem" co={14} />
      </button>
      {mo ? (
        <form className="nklt-tim__hop" role="search" onSubmit={gui}>
          <input
            type="search"
            aria-label={t.timTheoTenMa}
            placeholder={t.nhapTenMa}
            value={oTim}
            autoFocus
            onChange={(e) => datOTim(e.target.value)}
          />
          <button type="submit" className="nut nut--chinh">
            {t.tim}
          </button>
          {tuKhoa ? (
            <button
              type="button"
              className="nut"
              onClick={() => {
                datOTim('');
                onTim('');
                datMo(false);
              }}
            >
              {t.boTim}
            </button>
          ) : null}
        </form>
      ) : null}
    </span>
  );
}

/* --------------------------------- Màn hình --------------------------------- */

type TrangThaiXuat =
  { loai: 'nghi' } | { loai: 'dang-xuat' } | { loai: 'xong'; thongBao: string; canhBao: boolean };

export function NhatKyLichPhatThietBi({
  api = API_NHAT_KY_LICH_THIET_BI_MAC_DINH,
}: NhatKyLichPhatThietBiProps = {}): ReactNode {
  const [nguonId, datNguonId] = useState('');
  const [tuKhoa, datTuKhoa] = useState('');
  const [tuNgay, datTuNgay] = useState('');
  const [denNgay, datDenNgay] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState<number>(SO_DONG_MAC_DINH);
  const [xuat, datXuat] = useState<TrangThaiXuat>({ loai: 'nghi' });

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayDiaBan({}, signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const dangLoc = nguonId !== '' || tuKhoa !== '' || tuNgay !== '' || denNgay !== '';

  /** Bộ lọc hiện tại (không gồm phân trang), dùng chung cho bảng và xuất tệp. */
  const boLoc: BoLocNhatKyLichThietBi = {
    nguonId: nguonId || undefined,
    keyword: tuKhoa || undefined,
    tuThoiDiem: ngayVnSangEpoch(tuNgay),
    denThoiDiem: ngayVnSangEpoch(denNgay, true),
  };

  const kq = dungTaiDuLieu<TrangDuLieu<DongNhatKyLichThietBi>>({
    tai: (signal) => api.nhatKy({ page: trang, pageSize: soDong, ...boLoc }, signal),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, nguonId, tuKhoa, tuNgay, denNgay],
  });

  /** Đổi bộ lọc thì quay về trang 1. */
  const doiLoc =
    <V,>(dat: (v: V) => void) =>
    (v: V): void => {
      dat(v);
      datTrang(1);
    };

  const xoaLoc = (): void => {
    datNguonId('');
    datTuKhoa('');
    datTuNgay('');
    datDenNgay('');
    datTrang(1);
  };

  const xuatExcel = async (): Promise<void> => {
    datXuat({ loai: 'dang-xuat' });
    try {
      const { soDong, tong } = await api.xuatExcel(boLoc);
      if (soDong === 0) {
        datXuat({ loai: 'xong', thongBao: t.xuatRong, canhBao: false });
        return;
      }
      const vuot = tong > soDong;
      datXuat({
        loai: 'xong',
        canhBao: vuot,
        thongBao: vuot
          ? dienVao(t.xuatVuotGioiHan, { tong, gioiHan: XUAT_TOI_DA_DONG, so: soDong })
          : dienVao(t.xuatXong, { so: soDong }),
      });
    } catch (e) {
      datXuat({
        loai: 'xong',
        canhBao: true,
        thongBao: dienVao(t.xuatLoi, { loi: e instanceof Error ? e.message : String(e) }),
      });
    }
  };

  const batDauStt = (trang - 1) * soDong;
  const dsDong = kq.duLieu?.items ?? [];

  const cot: CotBang<DongNhatKyLichThietBi>[] = [
    { khoa: 'stt', nhan: t.cot.stt, canGiua: true, ve: (d) => batDauStt + dsDong.indexOf(d) + 1 },
    {
      khoa: 'tenLich',
      nhan: t.cot.tenLich,
      phuTieuDe: (
        <TimTieuDe
          tuKhoa={tuKhoa}
          onTim={(k) => {
            datTuKhoa(k);
            datTrang(1);
          }}
        />
      ),
      ve: (d) => (
        <>
          <strong className="nklt-ten">{d.tenLichPhat ?? '—'}</strong>
          <span className="nklt-phu">{dienVao(t.maLich, { ma: d.lichPhatId })}</span>
        </>
      ),
    },
    {
      khoa: 'diaBan',
      nhan: t.cot.diaBan,
      ve: (d) => (
        <>
          <span>{d.tenDiaBan ?? '—'}</span>
          <span className="nklt-phu">{dienVao(t.thietBi, { ten: d.tenThietBi ?? d.cumLoaId })}</span>
        </>
      ),
    },
    {
      khoa: 'maDiaBan',
      nhan: t.cot.maDiaBan,
      ve: (d) => <span className="dslp-khong-xuong">{d.maDiaBan ?? '—'}</span>,
    },
    { khoa: 'soBanTin', nhan: t.cot.soBanTin, canGiua: true, ve: (d) => d.soBanTin ?? '—' },
    {
      khoa: 'thoiDiemGui',
      nhan: t.cot.thoiDiemGui,
      ve: (d) => <span className="dslp-khong-xuong">{gioNgayVn(d.thoiDiemGui)}</span>,
    },
  ];

  const dangXuat = xuat.loai === 'dang-xuat';

  return (
    <section className="man-hinh dslp" data-man-hinh="nhat-ky-lich-phat-thiet-bi">
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
      </header>

      <div className="dslp-loc" role="search">
        <TruongLoc nhan={tds.diaBan} htmlFor="nklt-dia-ban">
          <ChonDiaBanCay
            id="nklt-dia-ban"
            className="dslp-cay"
            nhan={tds.diaBan}
            giaTri={nguonId}
            cay={cay.duLieu ?? []}
            nhanTatCa={tds.toanTinh}
            goiYTim={tds.timDiaBan}
            onDoi={doiLoc(datNguonId)}
          />
        </TruongLoc>
        <OKhoangNgay
          tienTo="nklt"
          tuNgay={tuNgay}
          denNgay={denNgay}
          onDoiTu={doiLoc(datTuNgay)}
          onDoiDen={doiLoc(datDenNgay)}
          goiY={t.goiYKhoangNgay}
        />
        <button
          type="button"
          className="nut nklt-nut-excel"
          data-hanh-dong="xuat-excel"
          disabled={dangXuat}
          aria-busy={dangXuat}
          onClick={() => void xuatExcel()}
        >
          {dangXuat ? t.dangXuat : t.xuatExcel}
        </button>
      </div>

      {xuat.loai === 'xong' ? (
        <p
          className={`nklt-thong-bao${xuat.canhBao ? ' nklt-thong-bao--canh-bao' : ''}`}
          role="status"
          data-vung="ket-qua-xuat"
        >
          {xuat.thongBao}
        </p>
      ) : null}

      <BangDuLieu<DongNhatKyLichThietBi>
        nhan={t.tieuDe}
        cot={cot}
        dong={dsDong}
        khoaDong={(d) => d.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={t.rongMoTa}
      />
    </section>
  );
}
